import { useEffect, useState } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import IntakeToolForm from '../components/IntakeToolForm';
import SurveyToolResponse from '../components/SurveyToolResponse';
import ExportSurvey from '../components/ExportSurvey';
import CenteredQuestion, { type QuestionData } from '../components/CenteredQuestion';
import IntakeWelcome from '../components/IntakeWelcome';
import PasswordInput from '../components/PasswordInput';
import LoadingOverlay from '../components/LoadingOverlay';
import ErrorToast from '../components/ErrorToast';
import Button from '../components/common/Button';
import SessionPasswordCode from '../components/SessionPasswordCode';
import { useTranslate, useLanguage } from '../lib/i18n';
import { useCreateSession, useCreateAuthSession, useConfig } from '../hooks';
import analyticsService from '../services/analyticsService';
import {
  getConversationMessages,
  getSessionConversations,
  createReturnCode,
  type AISurveyAnswerResponse,
} from '../services/backendService';
import {
  ApiSurveyQuestion,
  Survey,
  SurveyQuestion,
  getSurveyQuestions,
} from '../services/surveyBackendService';
import { ApiError } from '../lib/api';

interface QuestionWithAnswer extends SurveyQuestion {
  answer?: string | string[];
}

interface CenteredSurveyProps {
  questions: QuestionData[];
  currentQuestionIndex: number;
  onOptionSelect: (optionIndex: number) => void;
}

const CenteredSurvey = ({
  questions,
  currentQuestionIndex,
  onOptionSelect,
}: CenteredSurveyProps) => {
  const currentQuestion = questions[currentQuestionIndex];

  return (
    <>
      {currentQuestion && (
        <CenteredQuestion question={currentQuestion} onOptionSelect={onOptionSelect} />
      )}
    </>
  );
};

type IntakeState = 'password-welcome' | 'password-input' | 'survey' | 'feedback' | 'export';

const transformApiQuestions = (
  questions: ApiSurveyQuestion[],
  t: (key: string) => string
): SurveyQuestion[] => {
  return questions.map((q, index) => {
    const order = index + 1;
    const section =
      order <= 4
        ? t('survey.victimProfile')
        : order <= 16
          ? t('survey.crimeOverview')
          : t('survey.helpSources');

    return {
      questionType: q.dataType === 'selectMultiple' ? 'multipleChoice' : 'singleChoice',
      order,
      isRequired: q.required,
      answerOptions: q.answerOptions.map((optionKey) => ({
        mainQuestion: t(optionKey),
        translationKey: optionKey,
      })),
      mainText: t(`${q.analysisKey}Question`),
      helpText: t(`${q.analysisKey}`),
      section,
    };
  });
};

const IntakeToolSurvey = () => {
  const [intakeState, setIntakeState] = useState<IntakeState>('password-welcome');
  const [showCenteredSurvey, setShowCenteredSurvey] = useState(false);
  const [currentMandatoryQuestionIndex, setCurrentMandatoryQuestionIndex] = useState(0);
  const [questionsWithAnswers, setQuestionsWithAnswers] = useState<QuestionWithAnswer[]>([]);
  const [userProfileTags, setUserProfileTags] = useState<Record<string, boolean>>({
    underage: false,
    domestic: false,
  });
  const [isInitializing, setIsInitializing] = useState(false);
  const [sessionPassword, setSessionPassword] = useState<{
    code: string;
    expiresAt: string | null;
  } | null>(null);
  const [feedback, setFeedback] = useState<AISurveyAnswerResponse | null>(null);
  const [feedbackCreatedAt, setFeedbackCreatedAt] = useState<Date | null>(null);
  const [feedbackSource, setFeedbackSource] = useState<'survey' | 'password' | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [warnOnUnload, setWarnOnUnload] = useState(false);
  const [isBackConfirmOpen, setIsBackConfirmOpen] = useState(false);

  const t = useTranslate();
  const { language } = useLanguage();
  const { mutateAsync: createAuthSession } = useCreateAuthSession();
  const { mutateAsync: createSession } = useCreateSession();
  const { data: chatbotConfig, isLoading: configLoading } = useConfig();

  const {
    data: apiQuestions,
    isLoading: questionsLoading,
    error,
  } = useQuery<ApiSurveyQuestion[], ApiError>({
    queryKey: ['surveyQuestions', chatbotConfig?.answersSurveyId],
    queryFn: () =>
      getSurveyQuestions({
        surveyId: chatbotConfig!.answersSurveyId!,
      }),
    enabled: !!chatbotConfig?.answersSurveyId,
    staleTime: 1000 * 60 * 60,
  });

  const isLoading = configLoading || questionsLoading;

  useEffect(() => {
    if (error) {
      console.error('Failed to load survey questions:', error);
      return;
    }

    if (!isLoading && apiQuestions && apiQuestions.length > 0) {
      const questions = transformApiQuestions(apiQuestions, t);
      setQuestionsWithAnswers(questions.map((q) => ({ ...q, answer: undefined })));
    }
  }, [apiQuestions, isLoading, error, t]);

  const handleContinueWithPassword = async (password: string) => {
    if (!chatbotConfig) return;

    setIsInitializing(true);
    setPasswordError(null);

    try {
      await createAuthSession({
        chatbotConfigId: chatbotConfig.id,
        password,
      });

      // Get all conversations for this session
      const conversations = await getSessionConversations();

      // Get the most recent conversation (first in sorted array)
      const latestConversation = conversations[0];

      if (!latestConversation) {
        setPasswordError(t('SurveyTool.passwordWelcome.passwordRetrievalFailed'));
        analyticsService.trackEvent({ eventName: 'returnCodeSubmitFailed' });
        setIntakeState('password-welcome');
        setIsInitializing(false);
        return;
      }

      // Fetch all messages for the latest conversation
      const messages = await getConversationMessages(latestConversation.id);

      // Filter to get the last ASSISTANT message
      const assistantMessages = messages.filter((m) => m.role === 'ASSISTANT');
      const lastAssistantMessage = assistantMessages[assistantMessages.length - 1];

      if (lastAssistantMessage) {
        // Parse the content to get the survey response
        const previousResponse: AISurveyAnswerResponse = JSON.parse(lastAssistantMessage.content);
        setFeedback(previousResponse);
        setFeedbackCreatedAt(new Date(latestConversation.createdAt));

        setSessionPassword({
          code: password.slice(11),
          expiresAt: null,
        });

        setFeedbackSource('password');
        setWarnOnUnload(true);
        setIntakeState('feedback');
        analyticsService.trackEvent({ eventName: 'returnCodeSubmitSuccess' });
      } else {
        // Single generic error for any failure reason
        setPasswordError(t('SurveyTool.passwordWelcome.passwordRetrievalFailed'));
        analyticsService.trackEvent({ eventName: 'returnCodeSubmitFailed' });
        setIntakeState('password-welcome');
      }
    } catch (error) {
      console.error('Failed to initialize with password:', error);
      // Single generic error for any failure reason
      setPasswordError(t('SurveyTool.passwordWelcome.passwordRetrievalFailed'));
      analyticsService.trackEvent({ eventName: 'returnCodeSubmitFailed' });
      setIntakeState('password-welcome');
    } finally {
      setIsInitializing(false);
    }
  };

  const handleStartNew = async () => {
    if (!chatbotConfig) return;

    resetIntakeFlow();
    setIsInitializing(true);
    try {
      await createAuthSession({
        chatbotConfigId: chatbotConfig.id,
      });

      await createSession({
        gender: 'other',
        consent: true,
        language,
      });

      setIntakeState('survey');
      setShowCenteredSurvey(true);
    } catch (error) {
      console.error('Failed to initialize session:', error);
    } finally {
      setIsInitializing(false);
    }
  };

  const handleContinueWithPasswordClick = () => {
    setIntakeState('password-input');
  };

  const handlePasswordInputSubmit = (data: { selectedDate: Date; passwordValues: string[] }) => {
    // Construct password in format: dd-mm-yyyy-word1-word2-word3
    const day = data.selectedDate.getDate().toString().padStart(2, '0');
    const month = (data.selectedDate.getMonth() + 1).toString().padStart(2, '0');
    const year = data.selectedDate.getFullYear().toString();
    const dateString = `${day}-${month}-${year}`;
    const wordsString = data.passwordValues.join('-');
    const password = `${dateString}-${wordsString}`;

    handleContinueWithPassword(password);
  };

  const resetSurveyState = () => {
    setQuestionsWithAnswers((prev) => prev.map((q) => ({ ...q, answer: undefined })));
    setCurrentMandatoryQuestionIndex(0);
    setShowCenteredSurvey(true);
    setUserProfileTags({
      underage: false,
      domestic: false,
    });
  };

  const resetIntakeFlow = () => {
    resetSurveyState();
    setFeedback(null);
    setFeedbackCreatedAt(null);
    setSessionPassword(null);
    setFeedbackSource(null);
  };

  const handleSurveySubmitted = async (
    feedbackResponse: AISurveyAnswerResponse,
    conversationCreatedAt: Date
  ) => {
    setFeedback(feedbackResponse);
    setFeedbackCreatedAt(conversationCreatedAt);
    setFeedbackSource('survey');
    setWarnOnUnload(true);

    try {
      const { code, expiresAt } = await createReturnCode();
      setSessionPassword({
        code: code.slice(11),
        expiresAt,
      });
      setIntakeState('feedback');
    } catch (error) {
      console.error('Failed to create return code:', error);
      setIntakeState('feedback');
    }
  };

  const getMandatoryQuestions = (survey: Survey): QuestionData[] => {
    return survey.questions
      .filter((q) => q.isRequired)
      .map((q) => ({
        question: q.mainText,
        options: q.answerOptions.map((option) => ({
          heading: option.mainQuestion || '',
          translationKey: option.translationKey || '',
          subtext: option.additionalInfo,
        })),
        helpText: q.helpText || undefined,
      }));
  };

  const handleMandatoryOptionSelect = (optionIndex: number) => {
    const currentQuestion = mandatoryQuestions[currentMandatoryQuestionIndex];
    if (!currentQuestion) return;

    const selectedOption = currentQuestion.options[optionIndex];
    if (!selectedOption) return;

    const answerKey = selectedOption.translationKey || selectedOption.heading;

    // TODO: remove this before a new client is onboarded
    if (answerKey === 'riku.ageMinor') {
      setUserProfileTags((prev) => ({ ...prev, underage: true }));
    } else if (
      currentQuestion.question === t('riku.ageQuestion') &&
      answerKey !== 'riku.ageMinor'
    ) {
      setUserProfileTags((prev) => ({ ...prev, underage: false }));
    }

    const fullSurveyIndex = questionsWithAnswers.findIndex(
      (q) => q.mainText === currentQuestion.question
    );
    if (fullSurveyIndex >= 0) {
      setQuestionsWithAnswers((prev) =>
        prev.map((q, index) => (index === fullSurveyIndex ? { ...q, answer: answerKey } : q))
      );
    }

    if (currentMandatoryQuestionIndex < mandatoryQuestions.length - 1) {
      setCurrentMandatoryQuestionIndex(currentMandatoryQuestionIndex + 1);
    } else {
      setShowCenteredSurvey(false);
      // State remains 'survey' - just transitioning from centered to main survey
    }
  };

  const handleSurveyAnswersChange = (answers: Record<number, string | string[]>) => {
    setQuestionsWithAnswers((prev) => {
      const updated = [...prev];

      updated.forEach((question, index) => {
        if (index in answers) {
          updated[index] = { ...question, answer: answers[index] };
        } else if (question.answer !== undefined) {
          updated[index] = { ...question, answer: undefined };
        }
      });

      return updated;
    });
  };

  const getAnswersForSurveyTool = (): Record<number, string | string[]> => {
    const answers: Record<number, string | string[]> = {};

    questionsWithAnswers.forEach((q, index) => {
      if (q.answer !== undefined) {
        answers[index] = q.answer;
      }
    });

    return answers;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-lg">{t('common.labels.loading')}</div>
          <div className="text-sm text-gray-500">{t('survey.loadingQuestions')}</div>
        </div>
      </div>
    );
  }

  // Check for missing configuration
  if (!chatbotConfig?.answersSurveyId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="max-w-md rounded-lg border border-yellow-200 bg-yellow-50 p-6 text-center">
          <h2 className="mb-2 text-lg font-semibold text-yellow-900">
            {t('survey.errors.configuration_missing')}
          </h2>
          <p className="text-sm text-yellow-700">{t('survey.errors.contact_support')}</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !apiQuestions || apiQuestions.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <h2 className="mb-2 text-lg font-semibold text-red-900">
            {t('survey.errors.loadFailed')}
          </h2>
          <p className="mb-4 text-sm text-red-700">
            {error ? t('survey.errors.apiError') : t('survey.errors.noQuestions')}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  // Trust API as single source of truth - no hardcoded fallback
  const currentSurvey: Survey = {
    name: '',
    description: '',
    questions: transformApiQuestions(apiQuestions, t),
  };

  const mandatoryQuestions = getMandatoryQuestions(currentSurvey);

  if (isInitializing) {
    return <LoadingOverlay />;
  }

  const shouldShowBackButton = intakeState !== 'password-welcome';

  const navigateBackFromFeedback = () => {
    if (feedbackSource === 'survey') {
      setIntakeState('survey');
    } else if (feedbackSource === 'password') {
      setIntakeState('password-input');
    } else {
      resetIntakeFlow();
      setIntakeState('password-welcome');
    }
  };

  const handleBackNavigation = () => {
    if (intakeState === 'password-input') {
      setPasswordError(null);
      setIntakeState('password-welcome');
      return;
    }

    if (intakeState === 'survey') {
      if (!showCenteredSurvey) {
        setShowCenteredSurvey(true);
        return;
      }

      if (currentMandatoryQuestionIndex > 0) {
        setCurrentMandatoryQuestionIndex((prev) => Math.max(prev - 1, 0));
        return;
      }

      resetIntakeFlow();
      setIntakeState('password-welcome');
      return;
    }

    if (intakeState === 'feedback') {
      setIsBackConfirmOpen(true);
      return;
    }

    if (intakeState === 'export') {
      setIntakeState('feedback');
    }
  };

  const backButton = shouldShowBackButton ? (
    <button
      type="button"
      onClick={handleBackNavigation}
      className="sticky top-4 left-4 z-40 ml-4 mt-4 w-fit inline-flex items-center gap-2 rounded-full border bg-background px-4 py-2 text-sm shadow-md text-foreground"
    >
      <ArrowLeftIcon className="size-4" />
      {t('common.buttons.back')}
    </button>
  ) : null;

  if (intakeState === 'export' && feedback && feedbackCreatedAt) {
    return (
      <>
        {backButton}
        <ExportSurvey
          feedback={feedback}
          sessionPassword={sessionPassword}
          createdAt={feedbackCreatedAt}
          onBack={() => {
            setWarnOnUnload(true);
            setIntakeState('feedback');
          }}
        />
      </>
    );
  }

  if (intakeState === 'feedback' && feedback) {
    return (
      <>
        {backButton}
        {isBackConfirmOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setIsBackConfirmOpen(false)}
            />
            <div className="relative z-10 w-full max-w-sm rounded-lg border bg-card p-4 shadow-lg">
              <h3 className="text-base font-semibold">{t('survey.leaveWarningTitle')}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t('survey.leaveWarningBody')}</p>
              <div className="mt-3">
                <SessionPasswordCode sessionPassword={sessionPassword} showTitle={false} />
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="secondary" onClick={() => setIsBackConfirmOpen(false)}>
                  {t('survey.leaveWarningStay')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setWarnOnUnload(false);
                    setIsBackConfirmOpen(false);
                    navigateBackFromFeedback();
                  }}
                >
                  {t('survey.leaveWarningLeave')}
                </Button>
              </div>
            </div>
          </div>
        )}
        <SurveyToolResponse
          feedback={feedback}
          onExport={() => {
            setIntakeState('export');
          }}
          userProfileTags={userProfileTags}
          sessionPassword={sessionPassword}
          warnOnUnload={warnOnUnload}
        />
      </>
    );
  }

  if (intakeState === 'password-welcome') {
    return (
      <>
        {backButton}
        <IntakeWelcome
          onContinueWithPassword={handleContinueWithPasswordClick}
          onStartNew={handleStartNew}
        />
        {passwordError && (
          <ErrorToast error={passwordError} onClose={() => setPasswordError(null)} />
        )}
      </>
    );
  }

  if (intakeState === 'password-input') {
    return (
      <>
        {backButton}
        <PasswordInput onSubmit={handlePasswordInputSubmit} />
      </>
    );
  }

  return (
    <>
      {backButton}
      {showCenteredSurvey ? (
        <CenteredSurvey
          questions={mandatoryQuestions}
          currentQuestionIndex={currentMandatoryQuestionIndex}
          onOptionSelect={handleMandatoryOptionSelect}
        />
      ) : (
        <IntakeToolForm
          survey={currentSurvey}
          answers={getAnswersForSurveyTool()}
          onAnswersChange={handleSurveyAnswersChange}
          userProfileTags={userProfileTags}
          onSurveySubmitted={handleSurveySubmitted}
          surveyQuestions={apiQuestions}
          surveyId={chatbotConfig!.answersSurveyId!}
        />
      )}
    </>
  );
};

export default IntakeToolSurvey;
